import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Linkedin, Pencil, Trash2, X } from "lucide-react";

import { Button, InputField } from "../../components/ui";
import type { GtmLead, GtmLeadSequence, GtmLeadSequenceStep } from "../api/GtmApi";
import {
  useGtmLeadSequenceDraftMutation,
  useGtmLeadSequenceMessagesPatchMutation,
  useGtmLeadSequenceQuery,
} from "../hooks/useGtmPlayLeads";

const MESSAGE_STEPS = new Set(["first", "follow_up_2", "follow_up_3"]);
const MAX_CHARS = 8000;

type Props = {
  projectId: string;
  leads: GtmLead[];
  selectedLeadId: string;
  onSelectLead: (leadId: string) => void;
  onClose: () => void;
  onRemoveFromSequence?: (leadId: string) => void;
  removing?: boolean;
};

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function sequenceStatusLabel(status: string, t: (s: string) => string): string {
  switch (status) {
    case "in_sequence":
    case "waiting":
      return t("Scheduled");
    case "review":
      return t("Accepted");
    case "hands":
      return t("Needs you");
    case "meeting":
      return t("Meeting");
    case "ended":
      return t("Lost");
    default:
      return status.replace(/_/g, " ");
  }
}

export function sequenceStatusPillClass(status: string): string {
  switch (status) {
    case "in_sequence":
    case "waiting":
      return "bg-sky-100 text-sky-800";
    case "review":
      return "bg-cyan-100 text-cyan-800";
    case "hands":
      return "bg-amber-100 text-amber-900";
    case "meeting":
      return "bg-emerald-100 text-emerald-800";
    case "ended":
      return "bg-rose-100 text-rose-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function stepStatusLabel(status: string, t: (s: string) => string) {
  if (status === "done") return t("Done");
  if (status === "current") return t("Current");
  return t("Upcoming");
}

/** Format API LocalDateTime (`2026-11-12T14:30:00`) without timezone shift. */
function formatStepWhen(iso: string | null | undefined, status: string, t: (s: string) => string): string | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const date = new Date(year, month - 1, day, hour, minute);
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  const time = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const dayLabel = sameDay
    ? t("Today")
    : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const when = `${dayLabel}, ${time}`;
  if (status === "done") return when;
  return `${t("Est.")} ${when}`;
}

export default function LeadSequenceMessagesPanel({
  projectId,
  leads,
  selectedLeadId,
  onSelectLead,
  onClose,
  onRemoveFromSequence,
  removing,
}: Props) {
  const { t } = useTranslation();
  const sequenceQuery = useGtmLeadSequenceQuery(projectId, selectedLeadId);
  const draftMutation = useGtmLeadSequenceDraftMutation(projectId);
  const patchMutation = useGtmLeadSequenceMessagesPatchMutation(projectId);
  const messagesRef = useRef<HTMLLIElement | null>(null);

  const [bodies, setBodies] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<string>("first");
  const [editing, setEditing] = useState<string | null>("first");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftAttemptedFor, setDraftAttemptedFor] = useState<string | null>(null);

  const seq = sequenceQuery.data;
  const selectedLead = leads.find((l) => l.id === selectedLeadId);
  const leadStatus = seq?.lead.status || selectedLead?.status || "in_sequence";

  useEffect(() => {
    setBodies({});
    setExpanded("first");
    setEditing("first");
    setSaveError(null);
    setDraftError(null);
    setDraftAttemptedFor(null);
  }, [selectedLeadId]);

  useEffect(() => {
    if (!seq) return;
    const next: Record<string, string> = {};
    for (const step of seq.steps) {
      if (MESSAGE_STEPS.has(step.step)) {
        next[step.step] = step.body || "";
      }
    }
    setBodies(next);
  }, [seq]);

  useEffect(() => {
    if (!seq || !selectedLeadId) return;
    if (draftAttemptedFor === selectedLeadId) return;
    if (draftMutation.isPending) return;
    const needsDraft = seq.steps.some(
      (s) => MESSAGE_STEPS.has(s.step) && s.editable && !(s.body || "").trim()
    );
    if (!needsDraft) {
      setDraftAttemptedFor(selectedLeadId);
      return;
    }
    setDraftAttemptedFor(selectedLeadId);
    draftMutation.mutate(selectedLeadId, {
      onError: (e: any) => {
        setDraftError(e?.response?.data?.message || e?.message || "Could not draft messages");
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seq, selectedLeadId]);

  const dirty = useMemo(() => {
    if (!seq) return false;
    return seq.steps.some((s) => {
      if (!MESSAGE_STEPS.has(s.step) || !s.editable) return false;
      return (bodies[s.step] ?? "") !== (s.body || "");
    });
  }, [seq, bodies]);

  const onSave = async () => {
    if (!seq) return;
    try {
      setSaveError(null);
      const messages = seq.steps
        .filter((s) => MESSAGE_STEPS.has(s.step) && s.editable)
        .map((s) => ({ step: s.step, body: (bodies[s.step] || "").slice(0, MAX_CHARS) }));
      await patchMutation.mutateAsync({ leadId: selectedLeadId, messages });
      setEditing(null);
    } catch (e: any) {
      setSaveError(e?.response?.data?.message || e?.message || "Could not save messages");
    }
  };

  const scrollToMessages = () => {
    setExpanded("first");
    messagesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const loading =
    sequenceQuery.isLoading ||
    (draftMutation.isPending && !seq?.steps.some((s) => (s.body || "").trim()));
  const loadError =
    (sequenceQuery.error as any)?.response?.data?.message ||
    (sequenceQuery.error as any)?.message ||
    draftError;

  const firstMessageIndex = (seq?.steps || []).findIndex((s) => MESSAGE_STEPS.has(s.step));

  return (
    <div className="fixed inset-0 z-40 flex bg-white">
      <aside className="flex w-64 shrink-0 flex-col border-r border-gray-200 bg-slate-50">
        <div className="flex items-center justify-between border-b border-gray-200 px-3 py-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("In sequence")}
            </div>
            <div className="text-sm font-semibold text-gray-900">{leads.length}</div>
          </div>
          <button
            type="button"
            className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            onClick={onClose}
            aria-label={t("Close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <ul className="flex-1 overflow-y-auto">
          {leads.map((lead) => {
            const active = lead.id === selectedLeadId;
            return (
              <li key={lead.id}>
                <button
                  type="button"
                  className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm ${
                    active
                      ? "bg-white font-medium text-gray-900 shadow-sm"
                      : "text-gray-700 hover:bg-white/70"
                  }`}
                  onClick={() => onSelectLead(lead.id)}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-xs font-semibold text-slate-700">
                    {lead.avatarUrl ? (
                      <img
                        src={lead.avatarUrl}
                        alt=""
                        className="h-8 w-8 rounded-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      initials(lead.name || lead.profileSlug)
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{lead.name || lead.profileSlug}</span>
                  <span
                    className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${sequenceStatusPillClass(
                      lead.status
                    )}`}
                  >
                    {sequenceStatusLabel(lead.status, t)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t("LinkedIn sequence")}
              </h2>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${sequenceStatusPillClass(
                  leadStatus
                )}`}
              >
                {sequenceStatusLabel(leadStatus, t)}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {seq?.statusBlurb || t("Loading sequence…")}
            </p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>
              {t("Back to leads")}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!dirty || patchMutation.isPending}
              isLoading={patchMutation.isPending}
              onClick={onSave}
            >
              {t("Save messages")}
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          <div className="min-w-0 flex-1 overflow-y-auto px-5 py-4">
            {loading ? (
              <p className="text-sm text-gray-500">{t("Drafting messages…")}</p>
            ) : loadError ? (
              <p className="text-sm text-red-600">{loadError}</p>
            ) : (
              <ol className="divide-y divide-gray-100">
                {(seq?.steps || []).map((step, index) => (
                  <JourneyStepRow
                    key={step.id}
                    ref={index === firstMessageIndex ? messagesRef : undefined}
                    step={step}
                    expanded={expanded === step.step}
                    editing={editing === step.step}
                    body={bodies[step.step] ?? step.body ?? ""}
                    onToggle={() =>
                      setExpanded((prev) => (prev === step.step ? "" : step.step))
                    }
                    onStartEdit={() => {
                      setExpanded(step.step);
                      setEditing(step.step);
                    }}
                    onBodyChange={(v) => setBodies((prev) => ({ ...prev, [step.step]: v }))}
                    t={t}
                  />
                ))}
              </ol>
            )}
            {saveError ? <p className="mt-3 text-sm text-red-600">{saveError}</p> : null}
          </div>

          <ProfileRail
            seq={seq}
            t={t}
            onManageMessages={scrollToMessages}
            onRemove={
              onRemoveFromSequence
                ? () => onRemoveFromSequence(selectedLeadId)
                : undefined
            }
            removing={removing}
          />
        </div>
      </main>
    </div>
  );
}

const JourneyStepRow = forwardRef<
  HTMLLIElement,
  {
    step: GtmLeadSequenceStep;
    expanded: boolean;
    editing: boolean;
    body: string;
    onToggle: () => void;
    onStartEdit: () => void;
    onBodyChange: (v: string) => void;
    t: (s: string) => string;
  }
>(function JourneyStepRow(
  { step, expanded, editing, body, onToggle, onStartEdit, onBodyChange, t },
  ref
) {
  const isMessage = step.kind === "message";

  const whenLabel = formatStepWhen(step.estimatedAt, step.status, t);

  return (
    <li ref={ref} className="py-3">
      <div className="flex items-center gap-3">
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${
            step.status === "done"
              ? "bg-emerald-500"
              : step.status === "current"
                ? "bg-blue-500"
                : "bg-gray-300"
          }`}
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900">{t(step.label)}</div>
          {whenLabel ? (
            <div className="mt-0.5 text-xs text-gray-500">{whenLabel}</div>
          ) : null}
        </div>
        <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {stepStatusLabel(step.status, t)}
        </span>
      </div>

      {isMessage ? (
        <div className="mt-2 pl-5">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
            onClick={onToggle}
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {expanded ? t("Hide message") : t("Show message")}
          </button>

          {expanded ? (
            <div className="mt-2 space-y-2">
              {step.editable ? (
                <>
                  <div className="relative rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="mb-2 flex items-center justify-end gap-2 text-xs text-gray-400">
                      <span>
                        {body.length}/{MAX_CHARS}
                      </span>
                      <button
                        type="button"
                        className="rounded p-1 text-gray-500 hover:bg-white hover:text-gray-800"
                        onClick={onStartEdit}
                        aria-label={t("Edit message")}
                        title={t("Edit message")}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {editing ? (
                      <InputField
                        mode="textarea"
                        rows={6}
                        value={body}
                        onChange={onBodyChange}
                        placeholder={t("Write the LinkedIn message…")}
                      />
                    ) : (
                      <p className="whitespace-pre-wrap text-sm text-gray-800">
                        {body || t("No draft yet.")}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {t(
                      "Drafted — sends when the sequence reaches this step. Written in your Brain voice."
                    )}
                  </p>
                </>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="whitespace-pre-wrap text-sm text-gray-800">
                    {step.sentBody || body || t("No message captured.")}
                  </p>
                  <p className="mt-2 text-xs text-gray-500">{t("Sent")}</p>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
});

function ProfileRail({
  seq,
  t,
  onManageMessages,
  onRemove,
  removing,
}: {
  seq: GtmLeadSequence | undefined;
  t: (s: string) => string;
  onManageMessages: () => void;
  onRemove?: () => void;
  removing?: boolean;
}) {
  const lead = seq?.lead;
  if (!lead) {
    return <aside className="hidden w-80 shrink-0 border-l border-gray-200 bg-slate-50 lg:block" />;
  }
  return (
    <aside className="hidden w-80 shrink-0 overflow-y-auto border-l border-gray-200 bg-slate-50 p-4 lg:block">
      <div className="flex items-start gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-sm font-semibold text-slate-700">
          {lead.avatarUrl ? (
            <img
              src={lead.avatarUrl}
              alt=""
              className="h-12 w-12 rounded-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            initials(lead.name)
          )}
        </span>
        <div className="min-w-0">
          <div className="truncate text-base font-semibold text-gray-900">{lead.name}</div>
          <div className="text-sm text-gray-600">
            {[lead.title, lead.company].filter(Boolean).join(" · ")}
          </div>
        </div>
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-3 text-sm">
        {lead.company ? (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("Company")}
            </dt>
            <dd className="text-gray-800">{lead.company}</dd>
          </div>
        ) : null}
        {lead.linkedinUrl ? (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("LinkedIn")}
            </dt>
            <dd>
              <a
                href={lead.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline"
              >
                <Linkedin className="h-3.5 w-3.5" />
                {t("Profile")}
              </a>
            </dd>
          </div>
        ) : null}
        {lead.location ? (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("Location")}
            </dt>
            <dd className="text-gray-800">{lead.location}</dd>
          </div>
        ) : null}
        {lead.email ? (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("Email")}
            </dt>
            <dd className="text-gray-800">{lead.email}</dd>
          </div>
        ) : null}
      </dl>

      <div className="mt-4 rounded-lg border border-sky-100 bg-sky-50 p-3">
        <div className="text-xs font-medium uppercase tracking-wide text-sky-700">
          {t("Sequence")}
        </div>
        <p className="mt-1 text-sm text-sky-950">{seq.statusBlurb}</p>
        <div className="mt-3 flex flex-col gap-2">
          <button
            type="button"
            className="text-left text-sm font-medium text-sky-800 hover:underline"
            onClick={onManageMessages}
          >
            {t("Manage sequence messages")} →
          </button>
          {onRemove ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-left text-sm text-rose-700 hover:underline disabled:opacity-50"
              disabled={removing}
              onClick={onRemove}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t("Remove from sequence")}
            </button>
          ) : null}
        </div>
      </div>

      {lead.whyFit ? (
        <div className="mt-4">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {t("Why they fit")}
          </div>
          <p className="mt-1 text-sm text-gray-700">{lead.whyFit}</p>
        </div>
      ) : null}
    </aside>
  );
}
